import { DEFAULT_PASSWORD_REGEX } from "@/utils/constants";

/** Character class matching `DEFAULT_PASSWORD_REGEX` allow-list (unanchored, one char). */
export const PASSWORD_ALLOWED_CHAR_REGEX =
  /[a-zA-Z\d=+\-^$*.[\]{}()?"!@#%&/\\,><':;|_~`]/;

/** Special characters accepted by the backend password policy. */
export const PASSWORD_SPECIAL_CHAR_REGEX =
  /[=+\-^$*.[\]{}()?"!@#%&/\\,><':;|_~`]/;

export type PasswordRequirementToken = {
  displayMessage: string;
  regex: RegExp;
};

type Translate = (key: string, options?: Record<string, string>) => string;

/**
 * Returns unique disallowed characters in order of first appearance.
 */
export const getDisallowedPasswordCharacters = (password: string): string[] => {
  const seen = new Set<string>();
  const disallowed: string[] = [];

  for (const char of password) {
    if (!PASSWORD_ALLOWED_CHAR_REGEX.test(char) && !seen.has(char)) {
      seen.add(char);
      disallowed.push(char);
    }
  }

  return disallowed;
};

export const formatDisallowedPasswordCharacter = (
  char: string,
  spaceLabel: string,
): string => (char === " " ? spaceLabel : char);

export const formatDisallowedPasswordCharactersMessage = (
  disallowed: string[],
  t: Translate,
): string => {
  const spaceLabel = t(
    "translation:newWelcome.registerForm.passwordRequirements.spaceCharacter",
  );
  const quoted = disallowed
    .map((char) => `"${formatDisallowedPasswordCharacter(char, spaceLabel)}"`)
    .join(", ");

  if (disallowed.length === 1) {
    return t(
      "translation:newWelcome.registerForm.passwordRequirements.disallowedCharacter",
      { character: quoted },
    );
  }

  return t(
    "translation:newWelcome.registerForm.passwordRequirements.disallowedCharacters",
    { characters: quoted },
  );
};

export const getPasswordRequirementTokens = (
  t: Translate,
): PasswordRequirementToken[] => [
  {
    regex: /^.{8,30}$/,
    displayMessage: t(
      "translation:newWelcome.registerForm.passwordRequirements.minLength",
    ),
  },
  {
    regex: PASSWORD_SPECIAL_CHAR_REGEX,
    displayMessage: t(
      "translation:newWelcome.registerForm.passwordRequirements.specialCharacter",
    ),
  },
  {
    regex: /(?=.*[a-z])(?=.*[A-Z])/,
    displayMessage: t(
      "translation:newWelcome.registerForm.passwordRequirements.upperLowercase",
    ),
  },
  {
    regex: /\d/,
    displayMessage: t(
      "translation:newWelcome.registerForm.passwordRequirements.numberRequired",
    ),
  },
];

/**
 * Field-level validation message stays as the general criteria hint.
 * Specific issues (e.g. disallowed characters) are shown in PasswordStrength.
 */
export const getPasswordValidationError = (
  password: string,
  t: Translate,
): true | string => {
  if (
    getDisallowedPasswordCharacters(password).length > 0 ||
    !DEFAULT_PASSWORD_REGEX.test(password)
  ) {
    return t("translation:newWelcome.registerForm.passwordHint");
  }

  return true;
};
